import React, { useState, useEffect } from 'react';
import { StreamingService } from '../services/streaming';

const WatchlistManager = () => {
  const [profile, setProfile] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [activeTab, setActiveTab] = useState('watchlist');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const data = await StreamingService.getProfile();
      setProfile(data);
      setWatchlist(data.watchlists || []);
      setFavorites(data.favorites || []);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromWatchlist = async (videoId) => {
    try {
      await StreamingService.removeFromWatchlist(videoId);
      setWatchlist(prev => prev.filter(id => id !== videoId));
    } catch (error) {
      alert('Failed to remove from watchlist');
    }
  };

  const addFavorite = async (videoId) => {
    try {
      await StreamingService.addFavorite(videoId);
      setFavorites(prev => [...prev, videoId]);
    } catch (error) {
      alert('Failed to add favorite');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-2xl text-white">Loading your content...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h1 className="text-5xl font-bold mb-8 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
        My Library
      </h1>

      {/* Tabs */}
      <div className="flex gap-4 mb-8">
        <button
          onClick={() => setActiveTab('watchlist')}
          className={`px-6 py-3 rounded-lg font-bold transition-all ${
            activeTab === 'watchlist'
              ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
              : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
          }`}
        >
          📋 Watchlist ({watchlist.length})
        </button>
        <button
          onClick={() => setActiveTab('favorites')}
          className={`px-6 py-3 rounded-lg font-bold transition-all ${
            activeTab === 'favorites'
              ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
              : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
          }`}
        >
          ❤️ Favorites ({favorites.length})
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-6 py-3 rounded-lg font-bold transition-all ${
            activeTab === 'history'
              ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
              : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
          }`}
        >
          📜 History
        </button>
      </div>

      {/* Content */}
      <div>
        {activeTab === 'watchlist' && (
          <div>
            {watchlist.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📭</div>
                <p className="text-gray-400 text-xl">Your watchlist is empty</p>
                <p className="text-gray-500 text-sm mt-2">Add videos to watch them later!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {watchlist.map((videoId, index) => (
                  <div key={index} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                    <div className="aspect-video bg-gray-800 rounded-lg mb-3 flex items-center justify-center text-4xl">
                      🎬
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-white font-bold">Video {videoId.slice(0, 8)}...</div>
                      <button
                        onClick={() => removeFromWatchlist(videoId)}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-sm font-bold transition-all"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'favorites' && (
          <div>
            {favorites.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">💔</div>
                <p className="text-gray-400 text-xl">No favorites yet</p>
                <p className="text-gray-500 text-sm mt-2">Mark videos as favorites to see them here!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favorites.map((videoId, index) => (
                  <div key={index} className="bg-gray-900/50 rounded-lg p-4 border border-red-500/50">
                    <div className="aspect-video bg-gray-800 rounded-lg mb-3 flex items-center justify-center text-4xl">
                      ❤️
                    </div>
                    <div className="text-white font-bold">Video {videoId.slice(0, 8)}...</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📜</div>
            <p className="text-gray-400 text-xl">Watch history coming soon</p>
          </div>
        )}
      </div>

      {/* Preferences */}
      {profile && (
        <div className="mt-12 bg-gray-900/50 rounded-lg p-6 border border-gray-700">
          <h2 className="text-2xl font-bold mb-4 text-white">Preferences</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg">
              <div className="text-gray-400 text-sm mb-1">Auto Play</div>
              <div className="text-white font-bold">{profile.preferences.auto_play ? 'On' : 'Off'}</div>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <div className="text-gray-400 text-sm mb-1">Quality</div>
              <div className="text-white font-bold">{profile.preferences.quality}</div>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <div className="text-gray-400 text-sm mb-1">Subtitles</div>
              <div className="text-white font-bold">{profile.preferences.subtitles ? 'On' : 'Off'}</div>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg">
              <div className="text-gray-400 text-sm mb-1">Mature Content</div>
              <div className="text-white font-bold">{profile.preferences.mature_content ? 'Allowed' : 'Blocked'}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WatchlistManager;